import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, SA_ORG_KEY } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Clapperboard, RefreshCw, Power } from "lucide-react";

export const DEMO_ORG_ID = "org_demo_sample";
export const DEMO_FLAG_KEY = "demo_mode";
export const isDemoMode = () => localStorage.getItem(DEMO_FLAG_KEY) === "1";

export const exitDemoMode = () => {
  localStorage.removeItem(DEMO_FLAG_KEY);
  const cur = localStorage.getItem(SA_ORG_KEY);
  if (cur === DEMO_ORG_ID || cur === "org_demo_fleet") localStorage.removeItem(SA_ORG_KEY);
  localStorage.removeItem("plant_id");
  window.location.assign("/dispatch");
};

export default function DemoModeCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const on = isDemoMode();

  useEffect(() => { api.get("/demo/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/demo/enable");
      localStorage.setItem(DEMO_FLAG_KEY, "1");
      localStorage.setItem(SA_ORG_KEY, DEMO_ORG_ID);
      localStorage.removeItem("plant_id");
      toast.success("Demo Mode ON — loading ArmorBatch Sample Plant");
      window.location.assign("/war-room");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try { await api.post("/demo/reset"); toast.success("Demo fleet reset — 14 trucks, 10 fresh jobs"); if (on) window.location.assign("/war-room"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className={`border bg-surface ${on ? "border-sky-500" : "border-industrial"}`} data-testid="demo-mode-card">
      <div className="px-5 py-4 border-b border-industrial flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Clapperboard className="w-4 h-4 text-sky-400" />
          <div>
            <div className="label-tag">DEMO MODE · FOR VIDEO & SALES WALKTHROUGHS</div>
            <h2 className="heading font-black uppercase text-base md:text-lg">ArmorBatch Sample Plant</h2>
          </div>
        </div>
        <button type="button" role="switch" aria-checked={on} data-testid="demo-mode-toggle" disabled={busy}
          onClick={() => (on ? exitDemoMode() : enable())}
          className={`relative inline-flex h-7 w-14 items-center rounded-full border transition-colors ${on ? "bg-sky-500 border-sky-400" : "bg-surface-2 border-industrial"}`}>
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-8" : "translate-x-1"}`} />
        </button>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
        <div className="text-sm text-muted-industrial space-y-1">
          <p><b className="text-ink">ON</b> hides your real organization and switches every screen to a sandbox tenant: <b className="text-ink">14 trucks</b> cycling Loading → En Route → On Site → Returning on the live map, <b className="text-ink">10 jobs</b> with progressive delivery, generic <b className="text-ink">ArmorBatch Sample Plant</b> branding, and <b className="text-ink">2 trucks (SP-13, SP-14) in Safety Lockout</b> flashing red.</p>
          <p>Nothing in your real data is modified. Turn it OFF (or use the header badge) to return instantly.</p>
          {status && <p className="mono text-[10px] uppercase tracking-wider" data-testid="demo-mode-status">seeded: {String(status.seeded)} · trucks {status.counts?.trucks} · jobs {status.counts?.jobs}{status.last_enabled_at ? ` · last enabled ${new Date(status.last_enabled_at).toLocaleString()}` : ""}</p>}
        </div>
        <div className="flex flex-col gap-2 min-w-[180px]">
          {on
            ? <Button data-testid="demo-mode-exit" variant="outline" className="rounded-none" onClick={exitDemoMode}><Power className="w-4 h-4 mr-2" />Exit Demo Mode</Button>
            : <Button data-testid="demo-mode-enable" className="rounded-none bg-sky-500 hover:bg-sky-400 text-black font-bold" disabled={busy} onClick={enable}><Clapperboard className="w-4 h-4 mr-2" />{busy ? "Loading…" : "Turn Demo Mode ON"}</Button>}
          <Button data-testid="demo-mode-reset" variant="ghost" className="rounded-none text-xs" disabled={busy} onClick={reset}><RefreshCw className="w-3.5 h-3.5 mr-1" />Reset demo fleet</Button>
        </div>
      </div>
    </div>
  );
}
